package com.heelion.farmafreelance.agent

import com.heelion.farmafreelance.agent.RisponditoreDomande.creatoreTipo
import com.heelion.farmafreelance.agent.RisponditoreDomande.formattaEuro
import com.heelion.farmafreelance.agent.RisponditoreDomande.formattaGiorno
import com.heelion.farmafreelance.agent.RisponditoreDomande.plurale
import com.heelion.farmafreelance.models.AnnuncioTurno
import com.heelion.farmafreelance.models.CreatoreTipo
import com.heelion.farmafreelance.models.Match
import com.heelion.farmafreelance.models.MatchStato
import com.heelion.farmafreelance.models.StoricoEntry
import com.heelion.farmafreelance.models.UserRole
import com.heelion.farmafreelance.models.isProfessionista
import com.heelion.farmafreelance.utils.TimeUtils
import com.heelion.farmafreelance.voice.VoiceCommandParser
import kotlinx.datetime.Instant
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlin.math.asin
import kotlin.math.cos
import kotlin.math.round
import kotlin.math.sin
import kotlin.math.sqrt

/**
 * Domande sui turni riconosciute con regole (il modello non ha questi intenti):
 * "quali farmacie mi hanno pagato di più?", "chi paga di più nella mia zona?",
 * "ci sono emergenze vicino a me?", "turni nelle farmacie vicino a me", "fammi il riepilogo dei miei turni".
 */
object DomandeTurni {
    // "apri l'agenda" è navigazione (la pagina), non una domanda.
    private val NAVIGAZIONE = Regex("^(apri|aprimi|vai|portami|torna|spostami)\\b")
    // Frasi che chiedono di FARE qualcosa: le gestiscono le azioni, non le domande.
    private val AZIONE = Regex("\\b(accett|prend|candid|prenot|rifiut|disdi|cancell|elimin|pubblic|avvisa|ricorda)\\w*")
    private val PIU_PAGATO = Regex(
        "\\bpag\\w* (di )?piu\\b|\\bpiu pagat|\\bpag\\w* meglio|\\bmeglio pagat|\\bcompens\\w* piu alt|\\bpaga piu alta|\\bpiu soldi|\\bpiu generos"
    )
    // "...mi hanno pagato", "per cui ho lavorato": si parla dei propri turni svolti, non dei turni aperti.
    private val MIEI = Regex("\\bmi (ha|hanno)\\b|\\bho (lavorato|fatto|svolto)\\b|\\bper cui\\b|\\bdove ho\\b|\\bpresso cui\\b")
    private val EMERGENZE = Regex("\\b(emergenz\\w*|urgent\\w*|sos)\\b")
    private val VICINO = Regex("\\bvicin\\w*|\\bnei (dintorni|paraggi)\\b|\\b(intorno|attorno) a me\\b|\\bqui (intorno|attorno)\\b")
    // "cerca farmacisti/infermieri vicino a me" è la ricerca professionisti; "il mio turno più vicino" è il prossimo turno.
    private val NON_TURNI_VICINI = Regex("\\bfarmacist|\\binfermier|\\b(mio|miei|mia|mie)\\b")
    private val AGENDA = Regex(
        "\\bagenda\\b|\\b(riepilog\\w*|riassum\\w*|riassunt\\w*|recap|resoconto)\\b.*\\bturn" +
            "|\\bturni (passati|fatti|svolti|futuri|accettati)\\b" +
            "|\\b(quali|che) turni (ho (gia )?(fatto|svolto|accettato)|devo (ancora )?fare)\\b"
    )

    fun tipo(testo: String): TipoDomanda? {
        val t = IntentClassifier.normalizza(testo)
        if (NAVIGAZIONE.containsMatchIn(t) || AZIONE.containsMatchIn(t)) return null
        return when {
            PIU_PAGATO.containsMatchIn(t) && MIEI.containsMatchIn(t) -> TipoDomanda.FARMACIE_PIU_PAGANTI
            PIU_PAGATO.containsMatchIn(t) -> TipoDomanda.TURNI_PIU_PAGATI
            EMERGENZE.containsMatchIn(t) -> TipoDomanda.EMERGENZE
            VICINO.containsMatchIn(t) && !NON_TURNI_VICINI.containsMatchIn(t) -> TipoDomanda.TURNI_VICINI
            AGENDA.containsMatchIn(t) -> TipoDomanda.AGENDA
            else -> null
        }
    }

    /** "vicino a me", "nei dintorni": la domanda chiede di ordinare per distanza. */
    fun vicino(testo: String): Boolean = VICINO.containsMatchIn(IntentClassifier.normalizza(testo))
}

/** Dove cercare i turni aperti: [provincia] per il server (sigle, anche "MI,BG"), [comune] filtrato qui. */
data class LuogoRichiesto(val provincia: String?, val comune: String?, val detto: Boolean)

object LuogoTurni {
    /** "a Milano" -> MI; "a Rho" -> comune Rho (in tutta Italia); niente -> la zona del profilo. */
    fun da(testo: String, zona: String?): LuogoRichiesto {
        val provinciaDetta = VoiceCommandParser.provinciaMenzionata(testo)?.let { Province.sigla(it) ?: it }
        val comuneDetto = VoiceCommandParser.comuneMenzionato(testo)
        return when {
            provinciaDetta != null -> LuogoRichiesto(provinciaDetta, null, true)
            comuneDetto != null && Province.sigla(comuneDetto) != null -> LuogoRichiesto(Province.sigla(comuneDetto), null, true)
            comuneDetto != null -> LuogoRichiesto(null, comuneDetto, true)
            else -> LuogoRichiesto(zona, null, false)
        }
    }

    fun nelComune(a: AnnuncioTurno, comune: String): Boolean {
        val c = IntentClassifier.normalizza(comune)
        return listOfNotNull(a.localitaCreatore, a.zonaTestuale).any { IntentClassifier.normalizza(it).contains(c) }
    }

    fun descrivi(l: LuogoRichiesto): String = when {
        l.comune != null -> " a ${l.comune}"
        l.provincia != null -> if (l.detto) " in provincia di ${l.provincia}" else " nella tua zona (${l.provincia})"
        else -> " in tutta Italia"
    }
}

/** Una farmacia per cui il farmacista ha lavorato, con quanto ha guadagnato lì. */
data class GuadagnoFarmacia(val nome: String, val turni: Int, val ore: Double, val totale: Double, val daIncassare: Double) {
    val mediaOraria: Double get() = if (ore > 0) totale / ore else 0.0
}

object ClassificaFarmacie {
    /** Dallo storico: per farmacia turni, ore e totale (ore x tariffa, come guadagni e fatture), dal totale più alto. */
    fun perGuadagno(storico: List<StoricoEntry>): List<GuadagnoFarmacia> =
        storico.groupBy { it.farmaciaId }.map { (_, turni) ->
            GuadagnoFarmacia(
                nome = turni.firstNotNullOfOrNull { it.nomeFarmacia } ?: "Farmacia",
                turni = turni.size,
                ore = turni.sumOf { TimeUtils.calculateHours(it.oraInizio, it.oraFine) },
                totale = arrotonda(turni.sumOf { ResocontoPagamenti.importo(it) }),
                daIncassare = arrotonda(turni.filter { it.pagatoIl == null }.sumOf { ResocontoPagamenti.importo(it) })
            )
        }.sortedByDescending { it.totale }

    fun risposta(ruolo: UserRole?, storico: List<StoricoEntry>?): String {
        if (ruolo?.isProfessionista != true) return "Questa classifica è per farmacisti e infermieri."
        if (storico == null) return "Non riesco a caricare i turni svolti in questo momento: controlla la connessione e riprova."
        val classifica = perGuadagno(storico)
        if (classifica.isEmpty()) return "Non hai ancora turni svolti: appena ne farai ti dirò quali farmacie ti pagano di più."
        val elenco = classifica.take(5).mapIndexed { i, f ->
            "${i + 1}) ${f.nome}: ${formattaEuro(f.totale)} in ${plurale(f.turni, "turno", "turni")}, " +
                "${formattaEuro(f.mediaOraria)}/h di media" +
                if (f.daIncassare > 0) " (${formattaEuro(f.daIncassare)} ancora da incassare)" else ""
        }.joinToString("; ")
        val migliore = classifica.maxBy { it.mediaOraria }
        val oraria = if (classifica.size > 1 && migliore != classifica.first()) {
            " All'ora paga meglio ${migliore.nome}, ${formattaEuro(migliore.mediaOraria)}/h."
        } else ""
        return "Le farmacie per cui hai lavorato, da quella che ti ha pagato di più: $elenco." +
            (if (classifica.size > 5) " E altre ${classifica.size - 5}." else "") + oraria
    }
}

object TurniMeglioPagati {
    /** I turni aperti della zona dal compenso orario più alto; a pari compenso il più vicino nel tempo. */
    fun ordina(annunci: List<AnnuncioTurno>): List<AnnuncioTurno> =
        annunci.filter { (it.compenso ?: 0.0) > 0 }.sortedWith(compareByDescending<AnnuncioTurno> { it.compenso }.thenBy { it.dataTurno })

    fun risposta(
        ctx: ContestoUtente, annunci: List<AnnuncioTurno>?, testo: String, oggi: LocalDate, tz: TimeZone
    ): String {
        if (ctx.ruolo?.isProfessionista != true) return "Questa domanda è per farmacisti e infermieri: ti dico quali farmacie pagano di più nei turni pubblicati."
        if (annunci == null) return NON_CARICATI
        val luogo = TurniVicini.luogo(testo, ctx.zona)
        // "chi paga di più vicino a me": solo le farmacie entro il raggio, con la distanza.
        val perDistanza = DomandeTurni.vicino(testo) && TurniVicini.usaDistanza(ctx, luogo)
        val aperti = RisponditoreDomande.annunciNuovi(ctx, annunci, oggi, tz)
            .filter { luogo.comune == null || LuogoTurni.nelComune(it, luogo.comune) }
            .filter { !perDistanza || (EmergenzeFarmy.distanza(ctx, it) ?: Double.MAX_VALUE) <= TurniVicini.RAGGIO_KM }
        val ordinati = ordina(aperti)
        val dove = if (perDistanza) TurniVicini.ENTRO_RAGGIO else LuogoTurni.descrivi(luogo)
        if (ordinati.isEmpty()) return "Al momento non ci sono turni aperti con un compenso indicato$dove."
        // Una riga per farmacia (il suo turno meglio pagato), così non si ripete la stessa farmacia.
        val perFarmacia = ordinati.distinctBy { it.creatoreId }
        val elenco = perFarmacia.take(5).mapIndexed { i, a ->
            "${i + 1}) ${descriviTurno(a, oggi, tz)}" +
                (if (perDistanza) EmergenzeFarmy.distanza(ctx, a)?.let { ", a ${EmergenzeFarmy.km(it)}" }.orEmpty() else "")
        }.joinToString("; ")
        return "Nei turni aperti$dove paga di più: $elenco." +
            (if (perFarmacia.size > 5) " E altre ${perFarmacia.size - 5} farmacie." else "") +
            " Se uno ti interessa dimmi per esempio \"accetta il turno della ${nomeBreve(perFarmacia.first())}\"."
    }
}

object EmergenzeFarmy {
    /** Come la Home: turni che iniziano tra 2 ore fa e le prossime 24 ore. */
    fun urgente(a: AnnuncioTurno, adesso: Instant): Boolean =
        (TimeUtils.combineDateAndTime(a.dataTurno, a.oraInizio) - adesso).inWholeHours in -2..24

    /** Distanza in km in linea d'aria (formula dell'emisenoverso). */
    fun distanzaKm(lat1: Double, lng1: Double, lat2: Double, lng2: Double): Double {
        val r = 6371.0
        val dLat = (lat2 - lat1) * kotlin.math.PI / 180
        val dLng = (lng2 - lng1) * kotlin.math.PI / 180
        val a = sin(dLat / 2) * sin(dLat / 2) +
            cos(lat1 * kotlin.math.PI / 180) * cos(lat2 * kotlin.math.PI / 180) * sin(dLng / 2) * sin(dLng / 2)
        return 2 * r * asin(sqrt(a))
    }

    fun distanza(ctx: ContestoUtente, a: AnnuncioTurno): Double? {
        val (lat, lng) = ctx.posizione ?: return null
        return distanzaKm(lat, lng, a.lat ?: return null, a.lng ?: return null)
    }

    /** Le emergenze ancora prenotabili: prima le più vicine (se si conosce la posizione), poi per ora d'inizio. */
    fun trova(ctx: ContestoUtente, annunci: List<AnnuncioTurno>, oggi: LocalDate, tz: TimeZone, adesso: Instant): List<AnnuncioTurno> =
        RisponditoreDomande.annunciNuovi(ctx, annunci, oggi, tz)
            .filter { urgente(it, adesso) }
            .sortedWith(compareBy<AnnuncioTurno> { distanza(ctx, it) ?: Double.MAX_VALUE }.thenBy { it.dataTurno })

    fun risposta(ctx: ContestoUtente, annunci: List<AnnuncioTurno>?, oggi: LocalDate, tz: TimeZone, adesso: Instant): String {
        val mio = ctx.ruolo?.creatoreTipo() ?: return "Accedi per vedere le emergenze."
        if (annunci == null) return NON_CARICATI
        val emergenze = trova(ctx, annunci, oggi, tz, adesso)
        val dove = if (ctx.posizione != null) " vicino a te" else ctx.zona?.let { " nella tua zona ($it)" } ?: ""
        if (emergenze.isEmpty()) return "Nessuna emergenza$dove nelle prossime 24 ore."
        val elenco = emergenze.take(3).joinToString("; ") { a ->
            descriviTurno(a, oggi, tz) + (distanza(ctx, a)?.let { ", a ${km(it)}" } ?: "")
        }
        val chi = RisponditoreDomande.controparti(mio)
        return "${plurale(emergenze.size, "emergenza", "emergenze")}$dove nelle prossime 24 ore " +
            "(turni di $chi da coprire subito): $elenco" +
            (if (emergenze.size > 3) "; e ${plurale(emergenze.size - 3, "altra", "altre")}." else ".") +
            " Per prenderne una dimmi \"accetta il turno urgente della ${nomeBreve(emergenze.first())}\"."
    }

    internal fun km(d: Double) = if (d < 1) "meno di 1 km" else "${round(d).toInt()} km"
}

object TurniVicini {
    /** Oltre questa distanza (in linea d'aria) una farmacia non è più "vicina". */
    const val RAGGIO_KM = 30.0
    const val ENTRO_RAGGIO = " entro 30 km da te"

    // "vicino a me", "da me": non sono un luogo (il parser leggerebbe "Me" come un comune).
    private val RIFERITO_A_ME = Regex("\\b(a|da) (me|te)\\b", RegexOption.IGNORE_CASE)

    fun luogo(testo: String, zona: String?): LuogoRichiesto = LuogoTurni.da(testo.replace(RIFERITO_A_ME, " "), zona)

    /** Distanza dalla posizione del profilo, se nota e se la domanda non nomina un altro luogo ("vicino a Rho"). */
    fun usaDistanza(ctx: ContestoUtente, luogo: LuogoRichiesto): Boolean = ctx.posizione != null && !luogo.detto

    /**
     * I turni aperti delle farmacie più vicine: con la posizione del profilo quelle entro [RAGGIO_KM]
     * dalla più vicina; senza, quelli della zona del profilo (o del luogo detto) dal primo in calendario.
     */
    fun risposta(ctx: ContestoUtente, annunci: List<AnnuncioTurno>?, testo: String, oggi: LocalDate, tz: TimeZone): String {
        if (ctx.ruolo?.isProfessionista != true) return "Questa domanda è per farmacisti e infermieri: ti dico quali farmacie vicine a te hanno turni aperti."
        if (annunci == null) return NON_CARICATI
        val luogo = luogo(testo, ctx.zona)
        val aperti = RisponditoreDomande.annunciNuovi(ctx, annunci, oggi, tz)
            .filter { luogo.comune == null || LuogoTurni.nelComune(it, luogo.comune) }

        if (!usaDistanza(ctx, luogo)) {
            val dove = LuogoTurni.descrivi(luogo)
            if (aperti.isEmpty()) return "Al momento non ci sono turni aperti$dove."
            val perFarmacia = aperti.distinctBy { it.creatoreId }
            val elenco = perFarmacia.take(5).mapIndexed { i, a -> "${i + 1}) ${descriviTurno(a, oggi, tz)}${altriTurni(a, aperti)}" }
                .joinToString("; ")
            val consiglio = if (!luogo.detto) " Se aggiungi l'indirizzo nel profilo te li ordino per distanza." else ""
            return "Turni aperti$dove, dal primo in calendario: $elenco." + altreFarmacie(perFarmacia.size) + consiglio
        }

        val conDistanza = aperti.mapNotNull { a -> EmergenzeFarmy.distanza(ctx, a)?.let { a to it } }
            .sortedWith(compareBy<Pair<AnnuncioTurno, Double>> { it.second }.thenBy { it.first.dataTurno })
        if (conDistanza.isEmpty()) return "Al momento non ci sono turni aperti vicino a te."
        val entro = conDistanza.filter { it.second <= RAGGIO_KM }
        if (entro.isEmpty()) {
            val (a, d) = conDistanza.first()
            return "Nessun turno aperto$ENTRO_RAGGIO. Il più vicino: ${descriviTurno(a, oggi, tz)}, a ${EmergenzeFarmy.km(d)}."
        }
        // Una riga per farmacia (il suo turno più vicino), dalla farmacia più vicina.
        val perFarmacia = entro.distinctBy { it.first.creatoreId }
        val turni = entro.map { it.first }
        val elenco = perFarmacia.take(5).mapIndexed { i, (a, d) ->
            "${i + 1}) ${descriviTurno(a, oggi, tz)}, a ${EmergenzeFarmy.km(d)}${altriTurni(a, turni)}"
        }.joinToString("; ")
        return "Turni aperti$ENTRO_RAGGIO, dalla farmacia più vicina: $elenco." + altreFarmacie(perFarmacia.size) +
            " Se uno ti interessa dimmi per esempio \"accetta il turno della ${nomeBreve(perFarmacia.first().first)}\"."
    }

    private fun altriTurni(a: AnnuncioTurno, turni: List<AnnuncioTurno>): String {
        val altri = turni.count { it.creatoreId == a.creatoreId } - 1
        return if (altri > 0) " (e ${plurale(altri, "altro turno", "altri turni")} lì)" else ""
    }

    private fun altreFarmacie(n: Int) = if (n > 5) " E altre ${n - 5} farmacie." else ""
}

enum class StatoAgenda(val etichetta: String) {
    CONFERMATO("confermato"), IN_ATTESA("in attesa di conferma"), SVOLTO("svolto")
}

/** Un turno dell'agenda: futuro (confermato o accettato e in attesa) o passato (svolto). */
data class VoceAgenda(
    val giorno: LocalDate,
    val oraInizio: String?,
    val oraFine: String?,
    val controparte: String,
    val luogo: String?,
    val stato: StatoAgenda,
    /** Ore x tariffa oraria, se conosciuta. */
    val importo: Double?,
    /** Solo per i turni svolti del farmacista: null = non si sa. */
    val pagato: Boolean? = null
)

data class Agenda(val daFare: List<VoceAgenda>, val passati: List<VoceAgenda>)

/**
 * Agenda dei turni: quelli da fare (confermati e accettati in attesa di conferma, dal più vicino)
 * e quelli svolti (dallo storico, dal più recente). Il giorno è la data UTC di dataTurno, come
 * nel resto dell'app.
 */
object AgendaTurni {
    fun costruisci(
        ruolo: UserRole?,
        matches: List<Match>,
        dettagli: Map<String, AnnuncioTurno>,
        storico: List<StoricoEntry>,
        oggi: LocalDate
    ): Agenda {
        fun giorno(i: Instant) = i.toLocalDateTime(TimeZone.UTC).date
        fun voceDaMatch(m: Match, stato: StatoAgenda): VoceAgenda? {
            val d = m.dataTurno?.let(::giorno) ?: return null
            val a = dettagli[m.annuncioId]
            return VoceAgenda(
                giorno = d, oraInizio = a?.oraInizio, oraFine = a?.oraFine,
                controparte = m.nomeControparte?.takeIf { it.isNotBlank() } ?: "Controparte",
                luogo = m.cittaControparte?.takeIf { it.isNotBlank() },
                stato = stato,
                importo = a?.compenso?.let { c -> arrotonda(TimeUtils.calculateHours(a.oraInizio, a.oraFine) * c) }
            )
        }

        val daFare = matches.mapNotNull { m ->
            when (m.stato) {
                MatchStato.CONFERMATO -> voceDaMatch(m, StatoAgenda.CONFERMATO)
                MatchStato.ACCETTATO -> voceDaMatch(m, StatoAgenda.IN_ATTESA)
                else -> null
            }
        }.filter { it.giorno >= oggi }.sortedWith(compareBy<VoceAgenda> { it.giorno }.thenBy { it.oraInizio })

        val svolti = storico.map { s ->
            VoceAgenda(
                giorno = giorno(s.dataTurno), oraInizio = s.oraInizio, oraFine = s.oraFine,
                controparte = (if (ruolo == UserRole.FARMACIA) s.nomeFarmacista else s.nomeFarmacia) ?: "Controparte",
                luogo = null, stato = StatoAgenda.SVOLTO,
                importo = arrotonda(ResocontoPagamenti.importo(s)),
                pagato = if (ruolo?.isProfessionista == true) s.pagatoIl != null else null
            )
        }
        // Turni confermati già passati ma non ancora nello storico: sono comunque stati fatti.
        val inStorico = storico.map { it.annuncioId to it.farmacistaId }.toSet()
        val passatiNonRegistrati = matches
            .filter { (it.stato == MatchStato.CONFERMATO || it.stato == MatchStato.COMPLETATO) && (it.annuncioId to it.farmacistaId) !in inStorico }
            .mapNotNull { voceDaMatch(it, StatoAgenda.SVOLTO) }
            .filter { it.giorno < oggi }
        val passati = (svolti + passatiNonRegistrati)
            .sortedWith(compareByDescending<VoceAgenda> { it.giorno }.thenByDescending { it.oraInizio })
        return Agenda(daFare, passati)
    }

    fun risposta(ctx: ContestoUtente, storico: List<StoricoEntry>?, oggi: LocalDate): String {
        if (ctx.ruolo?.creatoreTipo() == null) return "Accedi per vedere la tua agenda."
        val agenda = costruisci(ctx.ruolo, ctx.matches, ctx.dettagliConfermati, storico.orEmpty(), oggi)
        val parti = mutableListOf<String>()
        if (agenda.daFare.isEmpty()) {
            parti += "Non hai turni in programma."
        } else {
            val confermati = agenda.daFare.count { it.stato == StatoAgenda.CONFERMATO }
            val inAttesa = agenda.daFare.size - confermati
            val conteggio = listOfNotNull(
                confermati.takeIf { it > 0 }?.let { plurale(it, "confermato", "confermati") },
                inAttesa.takeIf { it > 0 }?.let { "$it in attesa di conferma" }
            ).joinToString(" e ")
            parti += "Da fare: ${plurale(agenda.daFare.size, "turno", "turni")} ($conteggio): " +
                agenda.daFare.take(4).joinToString("; ") { descrivi(it, oggi) } +
                (if (agenda.daFare.size > 4) "; e ${plurale(agenda.daFare.size - 4, "altro", "altri")}." else ".")
        }
        when {
            storico == null -> parti += "Non riesco a caricare i turni passati: controlla la connessione."
            agenda.passati.isEmpty() -> parti += "Non hai ancora turni svolti."
            else -> {
                val delMese = agenda.passati.filter { it.giorno.year == oggi.year && it.giorno.monthNumber == oggi.monthNumber }
                parti += "Passati: ${plurale(agenda.passati.size, "turno svolto", "turni svolti")}" +
                    (if (delMese.isNotEmpty()) ", di cui ${delMese.size} questo mese" else "") +
                    ". Gli ultimi: " + agenda.passati.take(3).joinToString("; ") { "${it.giorno.dayOfMonth}/${it.giorno.monthNumber} ${it.controparte}" } + "."
            }
        }
        return parti.joinToString(" ") + " Trovi tutto in Profilo > Agenda turni."
    }

    private fun descrivi(v: VoceAgenda, oggi: LocalDate): String = buildString {
        append(formattaGiorno(v.giorno, oggi))
        if (v.oraInizio != null && v.oraFine != null) append(" ${v.oraInizio}-${v.oraFine}")
        append(" con ${v.controparte}")
        if (v.stato == StatoAgenda.IN_ATTESA) append(" (in attesa di conferma)")
    }
}

/** "sabato 7 novembre 8:00-20:00, Farmacia Rossi (Milano), 30 €/h" */
internal fun descriviTurno(a: AnnuncioTurno, oggi: LocalDate, tz: TimeZone): String = buildString {
    a.nomeCreatore?.let { append(it) }
    (a.localitaCreatore ?: a.zonaTestuale)?.takeIf { it.isNotBlank() }?.let { append(" ($it)") }
    a.compenso?.takeIf { it > 0 }?.let { append(", ${formattaEuro(it)}/h") }
    append(", ${formattaGiorno(a.dataTurno.toLocalDateTime(tz).date, oggi)} ${a.oraInizio}-${a.oraFine}")
}

/** "Farmacia Rossi" -> "Farmacia Rossi"; senza nome "farmacia". */
private fun nomeBreve(a: AnnuncioTurno) = a.nomeCreatore?.takeIf { it.isNotBlank() } ?: "farmacia"

private const val NON_CARICATI = "Non riesco a caricare i turni aperti in questo momento: controlla la connessione e riprova."

private fun arrotonda(v: Double) = round(v * 100) / 100
